import axios from 'axios';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import {
  ErrorResponse,
  Loadable,
  TenderConfigOptions,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  AcceptRateQuoteRequest,
  BrokeragePayload,
  RateQuote,
  RateQuoteAcceptanceExceptionReason,
  RateSelectionReason,
  RateShop,
  RateShopStatus,
  Shipment,
} from '@/model';
import { getErrorDetail, isBadRequest } from '@/util';

const RATE_SHOP_POLL_INTERVAL = 500;
const RATE_SHOP_POLL_DURATION_MAX = 5 * 60 * 1000 + RATE_SHOP_POLL_INTERVAL;

type Callback = (() => void) | (() => Promise<void>);

type PolledRateShop = Loadable<RateShop> & {
  pollInterval?: ReturnType<typeof setTimeout>;
  pollTimeout?: ReturnType<typeof setTimeout>;
};

export interface UseShipmentRateShopResponse {
  /**
   * @deprecated
   * TODO: Get rid of this and the related endpoint
   */
  acceptBrokerageQuote: (
    {
      brokerageQuote,
      tenderConfigOptions,
      confirmTenderSentCallback,
    }: {
      brokerageQuote: RateQuote;
      tenderConfigOptions: TenderConfigOptions;
      confirmTenderSentCallback: () => void;
    },
    onComplete?: Callback,
  ) => Promise<void>;
  acceptRateQuote: (
    rateQuote: RateQuote,
    rateSelectionReason: RateSelectionReason | undefined,
    onComplete?: Callback,
  ) => Promise<void>;
  acceptedRateQuote: ComputedRef<RateQuote | undefined>;
  addBrokerageQuote: (
    { brokerageQuote, brokerageDates }: BrokeragePayload,
    onComplete?: Callback,
  ) => Promise<void>;
  addSpotQuote: (spotQuote: RateQuote, onComplete?: Callback) => Promise<void>;
  /**
   * @deprecated
   * TODO: Get rid of this and the related endpoint
   */
  deleteBrokerageQuote: (
    brokerageQuote: RateQuote,
    onComplete?: Callback,
  ) => Promise<void>;
  deleteSpotQuote: (
    spotQuote: RateQuote,
    onComplete?: Callback,
  ) => Promise<void>;
  loadRateShop: () => Promise<void>;
  /**
   * Rate shop data
   *
   * `loading` only reflects bulk operations (loadRateShop, updateRates).
   * Prefer `rateShopLoading` for UI bindings.
   **/
  rateShop: ComputedRef<Loadable<RateShop>>;
  /**
   * Unified loading state
   *
   * `true` when any bulk or per-quote operation is in-flight
   **/
  rateShopLoading: ComputedRef<boolean>;
  /**
   * @deprecated
   * TODO: Get rid of this and the related endpoint
   */
  updateBrokerageQuote: (
    { brokerageQuote, brokerageDates }: BrokeragePayload,
    onComplete?: Callback,
  ) => Promise<void>;
  updateRateQuote: (
    rateQuote: RateQuote,
    onComplete?: Callback,
  ) => Promise<void>;
  updateRates: (onComplete?: Callback) => Promise<void>;
}

export const useShipmentRateShop = (
  shipment: MaybeRefOrGetter<Shipment>,
): UseShipmentRateShopResponse => {
  const { t } = useI18n();
  const toast = useToast();

  const _rateShop: Ref<PolledRateShop> = ref({
    data: new RateShop(),
    loading: false,
  });

  const quoteLoadingState = ref<Set<number | symbol>>(new Set());

  const rateShop = computed(() => _rateShop.value);

  const rateShopLoading = computed(
    () => _rateShop.value.loading || quoteLoadingState.value.size > 0,
  );

  const acceptedRateQuote = computed((): RateQuote | undefined => {
    const rawShipment = toValue(shipment);
    const acceptedRateQuoteId = rawShipment.acceptedRateQuoteId;
    if (!acceptedRateQuoteId) {
      return;
    }

    return _rateShop.value.data?.rateQuotes?.find(
      (x) => x.id === acceptedRateQuoteId,
    );
  });

  const stopRateShopPolling = () => {
    clearInterval(_rateShop.value.pollInterval);
    _rateShop.value.pollInterval = undefined;
    clearTimeout(_rateShop.value.pollTimeout);
    _rateShop.value.pollTimeout = undefined;
  };

  const startRateShopPolling = () => {
    // no timeouts or intervals should exist for the current shipment
    // just to be safe, clear any existing polling for this shipment
    stopRateShopPolling();

    // start polling for rate shop completion
    _rateShop.value.pollInterval = setInterval(
      () => loadRateShop(),
      RATE_SHOP_POLL_INTERVAL,
    );

    // set a timeout to stop polling after a certain duration
    _rateShop.value.pollTimeout = setTimeout(() => {
      const rawShipment = toValue(shipment);
      console.warn(
        `Rate shop polling duration exceeded, shipmentId=${rawShipment.id}`,
      );
      stopRateShopPolling();

      _rateShop.value.loading = false;
    }, RATE_SHOP_POLL_DURATION_MAX);
  };

  const loadRateShop = async () => {
    const rawShipment = toValue(shipment);
    if (!rawShipment.id) {
      return;
    }

    if (!authorization.hasAuthority('rateShop', 'read')) {
      return;
    }

    try {
      _rateShop.value.error = undefined;
      _rateShop.value.loading = true;
      _rateShop.value.data = await api.shipment.loadRates(rawShipment.id);

      const status = _rateShop.value.data?.status;
      const isRateShopCompleted =
        status === RateShopStatus.COMPLETED || status === RateShopStatus.ERROR;

      if (isRateShopCompleted) {
        stopRateShopPolling();
        _rateShop.value.loading = false;
        return;
      }

      if (!_rateShop.value.pollInterval) {
        startRateShopPolling();
      }
    } catch (e) {
      _rateShop.value.error = e as Error;
      _rateShop.value.loading = false;

      if (axios.isCancel(e)) {
        return;
      }

      console.error(
        `error loading rate shop for shipment ${rawShipment.id}`,
        e,
      );
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('rateShop.rateShop'),
        }),
        error: e,
      });
    }
  };

  const updateRates = async (onComplete?: Callback) => {
    const rawShipment = toValue(shipment);
    if (!rawShipment.id) {
      return;
    }

    try {
      _rateShop.value.loading = true;
      _rateShop.value.data = await api.shipment.generateRates(rawShipment.id);
      onComplete?.();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error(`error updating rates for shipment ${rawShipment.id}`, e);
      _rateShop.value.error = e as Error;
      toast.addError({
        detail: t('errors.updatingParam', {
          param: t('rateShop.rates', 2),
        }),
        error: e,
      });
    } finally {
      _rateShop.value.loading = false;
    }
  };

  const acceptRateQuote = async (
    rateQuote: RateQuote,
    rateSelectionReason: RateSelectionReason | undefined,
    onComplete?: Callback,
  ) => {
    const rawShipment = toValue(shipment);
    if (!rawShipment.id) {
      return;
    }

    try {
      if (!rateQuote.id) {
        throw new Error('RateQuote does not have a valid id');
      }

      quoteLoadingState.value.add(rateQuote.id);

      if (rateQuote.source === 'BROKERAGE') {
        await api.shipment.acceptBrokerage(
          rawShipment.id,
          rateQuote.id,
          undefined,
        );
      } else {
        const request = new AcceptRateQuoteRequest({
          rateQuoteId: rateQuote.id,
          rateSelectionReason,
        });
        await api.shipment.acceptRateQuote(rawShipment.id, request);
      }

      toast.addSuccess({
        detail: t('rateShop.successAcceptingRateQuote'),
      });
      onComplete?.();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      let errorDetail = '';
      if (isBadRequest(e)) {
        const reasonCode = getErrorDetail(
          e,
          'reasonCode',
        ) as RateQuoteAcceptanceExceptionReason;
        if (reasonCode === RateQuoteAcceptanceExceptionReason.INACTIVE) {
          updateRates();
        }

        const errorResponse = e.response?.data as ErrorResponse;
        errorDetail = errorResponse.exceptionMessage;
      }

      console.error('Error accepting rate quote', e);
      _rateShop.value.error = e;
      toast.addError({
        detail: t('rateShop.errorAcceptingRateQuoteParam', {
          param: errorDetail ? errorDetail : '',
        }),
        error: e,
      });
    } finally {
      if (rateQuote.id) {
        quoteLoadingState.value.delete(rateQuote.id);
      }
    }
  };

  const updateRateQuote = async (
    rateQuote: RateQuote,
    onComplete?: Callback,
  ) => {
    const rawShipment = toValue(shipment);
    if (!rawShipment.id) {
      return;
    }

    try {
      if (!_rateShop.value.data) {
        throw new Error('Rate shop must be defined');
      }

      if (!rateQuote.id) {
        throw new Error('rateQuote id is required to update rate quote');
      }

      quoteLoadingState.value.add(rateQuote.id);
      await api.shipment.updateRateQuote(rawShipment.id, rateQuote);
      onComplete?.();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error updating rate quote', e);
      _rateShop.value.error = e as Error;
      toast.addError({
        detail: t('errors.updatingParam', {
          param: t('rateShop.quotes'),
        }),
        error: e,
      });
    } finally {
      if (rateQuote.id) {
        quoteLoadingState.value.delete(rateQuote.id);
      }
    }
  };

  const addSpotQuote = async (spotQuote: RateQuote, onComplete?: Callback) => {
    const rawShipment = toValue(shipment);
    if (!rawShipment.id) {
      return;
    }

    const quoteLoadingStateKey = spotQuote.id ?? Symbol();

    try {
      if (!_rateShop.value.data) {
        throw new Error('Rate shop must be defined');
      }

      quoteLoadingState.value.add(quoteLoadingStateKey);

      spotQuote.laneQuoteData
        ? await api.shipment.addLaneQuote(rawShipment.id, spotQuote)
        : await api.shipment.addSpotQuote(rawShipment.id, spotQuote);
      onComplete?.();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error saving spot quote', e);
      _rateShop.value.error = e as Error;
      toast.addError({
        detail: t('errors.savingParam', {
          param: t('rateShop.spotQuotes'),
        }),
        error: e,
      });
    } finally {
      quoteLoadingState.value.delete(quoteLoadingStateKey);
    }
  };

  const deleteSpotQuote = async (
    spotQuote: RateQuote,
    onComplete?: Callback,
  ) => {
    const rawShipment = toValue(shipment);
    if (!rawShipment.id || !spotQuote.id) {
      return;
    }

    try {
      if (!_rateShop.value.data) {
        throw new Error('Rate shop must be defined');
      }

      quoteLoadingState.value.add(spotQuote.id);

      await api.shipment.deleteSpotQuote(rawShipment.id, spotQuote.id);
      onComplete?.();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error deleting spot quote', e);
      _rateShop.value.error = e as Error;
      toast.addError({
        detail: t('errors.deletingParam', {
          param: t('rateShop.spotQuotes'),
        }),
        error: e,
      });
    } finally {
      quoteLoadingState.value.delete(spotQuote.id);
    }
  };

  const addBrokerageQuote = async (
    { brokerageQuote, brokerageDates, rateSelectionReason }: BrokeragePayload,
    onComplete?: Callback,
  ) => {
    const rawShipment = toValue(shipment);
    if (!rawShipment.id) {
      return;
    }

    const quoteLoadingStateKey = brokerageQuote.id ?? Symbol();

    try {
      if (!_rateShop.value.data) {
        throw new Error('Rate shop must be defined');
      }

      quoteLoadingState.value.add(quoteLoadingStateKey);

      await api.shipment.addBrokerage(
        rawShipment.id,
        brokerageQuote,
        brokerageDates,
        rateSelectionReason,
      );
      onComplete?.();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      let errorDetail = '';
      if (axios.isAxiosError(e)) {
        const errorResponse = e.response?.data as ErrorResponse;
        errorDetail = errorResponse.exceptionMessage;
      }

      //Pass on validation errors so field level validations can be displayed
      if (isBadRequest(e)) {
        throw e;
      }

      console.error('Error saving brokerage quote', e);
      _rateShop.value.error = e;
      toast.addError({
        detail: t('errors.savingParam', {
          param:
            t('brokerages.brokerages') +
            (errorDetail ? ` - ${errorDetail}` : ''),
        }),
        error: e,
      });
    } finally {
      quoteLoadingState.value.delete(quoteLoadingStateKey);
    }
  };

  const updateBrokerageQuote = async (
    { brokerageQuote, brokerageDates }: BrokeragePayload,
    onComplete?: Callback,
  ) => {
    const rawShipment = toValue(shipment);
    if (!rawShipment.id) {
      return;
    }

    try {
      if (!_rateShop.value.data) {
        throw new Error('Rate shop must be defined');
      }

      if (!brokerageQuote.id) {
        throw new Error('brokerage id is required to update brokerage quote');
      }

      quoteLoadingState.value.add(brokerageQuote.id);
      await api.shipment.updateBrokerage(
        rawShipment.id,
        brokerageQuote,
        brokerageDates,
      );
      onComplete?.();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error updating brokerage quote', e);
      _rateShop.value.error = e as Error;
      toast.addError({
        detail: t('errors.updatingParam', {
          param: t('brokerages.brokerages'),
        }),
        error: e,
      });
    } finally {
      if (brokerageQuote.id) {
        quoteLoadingState.value.delete(brokerageQuote.id);
      }
    }
  };

  const deleteBrokerageQuote = async (
    brokerageQuote: RateQuote,
    onComplete?: Callback,
  ) => {
    const rawShipment = toValue(shipment);
    if (!rawShipment.id || !brokerageQuote.id) {
      return;
    }

    try {
      if (!_rateShop.value.data) {
        throw new Error('Rate shop must be defined');
      }

      quoteLoadingState.value.add(brokerageQuote.id);
      await api.shipment.deleteBrokerage(rawShipment.id, brokerageQuote.id);
      onComplete?.();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error deleting brokerage quote', e);
      _rateShop.value.error = e as Error;
      toast.addError({
        detail: t('errors.deletingParam', {
          param: t('brokerages.brokerages'),
        }),
        error: e,
      });
    } finally {
      quoteLoadingState.value.delete(brokerageQuote.id);
    }
  };

  const acceptBrokerageQuote = async (
    {
      brokerageQuote,
      tenderConfigOptions,
      confirmTenderSentCallback,
    }: {
      brokerageQuote: RateQuote;
      tenderConfigOptions: TenderConfigOptions;
      confirmTenderSentCallback: () => void;
    },
    onComplete?: Callback,
  ) => {
    const rawShipment = toValue(shipment);
    if (!rawShipment.id) {
      return;
    }

    try {
      if (!brokerageQuote.id) {
        throw new Error('Brokerage quote does not have a valid id');
      }

      quoteLoadingState.value.add(brokerageQuote.id);
      await api.shipment.acceptBrokerage(
        rawShipment.id,
        brokerageQuote.id,
        tenderConfigOptions,
      );
      confirmTenderSentCallback();
      onComplete?.();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      let errorDetail = '';
      if (axios.isAxiosError(e)) {
        const errorResponse = e.response?.data as ErrorResponse;
        errorDetail = errorResponse.exceptionMessage;
      }

      console.error('Error accepting brokerage quote', e);
      _rateShop.value.error = e;
      toast.addError({
        detail: t('rateShop.errorAcceptingRateQuoteParam', {
          param: errorDetail ? errorDetail : '',
        }),
        error: e,
      });
    } finally {
      if (brokerageQuote.id != null) {
        quoteLoadingState.value.delete(brokerageQuote.id);
      }
    }
  };

  return {
    acceptBrokerageQuote,
    acceptRateQuote,
    acceptedRateQuote,
    addBrokerageQuote,
    addSpotQuote,
    deleteBrokerageQuote,
    deleteSpotQuote,
    loadRateShop,
    rateShop,
    rateShopLoading,
    updateBrokerageQuote,
    updateRateQuote,
    updateRates,
  };
};
